import { useTranslation } from "next-i18next";
import { FaCheck } from "react-icons/fa";
import { cn } from "../../../../../lib/utils";
import { Line } from "../../../casos/components/Line";
import { Subtitle } from "../../../casos/components/Subtitle";
import { Text } from "../../../casos/components/Text";

function HighlightedText({ text, highlight, className }) {
  if (!highlight || !text || !text.includes(highlight)) {
    return <span className={className}>{text}</span>;
  }

  const parts = text.split(highlight);

  return (
    <span className={className}>
      {parts[0]}
      <strong className="font-semibold text-vivid-03">{highlight}</strong>
      {parts[1]}
    </span>
  );
}

function ModelWrapper({ children, title, titleHighlight, className }) {
  const renderTitle = () => {
    if (titleHighlight && title.includes(titleHighlight)) {
      const parts = title.split(titleHighlight);
      return (
        <>
          {parts[0]}
          <strong className="font-bold">{titleHighlight}</strong>
          {parts[1]}
        </>
      );
    }
    return title;
  };

  return (
    <div className="relative flex-1 flex flex-col border border-white rounded-lg bg-background-01">
      <div
        className={cn(
          "flex items-center justify-center rounded-lg mx-auto px-4",
          "max-w-[310px] h-[48px] w-full",
          "laptop:h-[64px] laptop:max-w-[420px]",
          "laptopL:max-w-[520px]",
          "top-0 absolute -translate-y-1/2 left-1/2 -translate-x-1/2",
          className
        )}
      >
        <span className="text-inherit text-lg laptop:text-xl font-medium leading-6 tracking-[0.06px] text-center">
          {renderTitle()}
        </span>
      </div>

      {children}
    </div>
  );
}

function ModelCard({ title, titleHighlight, subtitle, sections, result, variant = "primary" }) {
  const isPrimary = variant === "primary";

  return (
    <ModelWrapper
      title={title}
      titleHighlight={titleHighlight}
      className={isPrimary ? "bg-vivid-03 text-neutral-08" : "bg-white text-neutral-08"}
    >
      <div
        className={cn(
          "flex flex-col text-left flex-1",
          "laptop:px-6 laptop:pb-8 laptop:mt-14",
          "px-4 pb-6 mt-10"
        )}
      >
        <p
          className={cn(
            "text-neutral-03 text-base laptop:text-lg leading-[26px] mb-6"
          )}
        >
          {subtitle}
        </p>

        <div className="flex-1">
          {sections.map((section, idx) => (
            <div key={idx} className="mb-6">
              <h4
                className={cn(
                  "text-white font-medium text-lg leading-[30px] mb-3",
                  "laptop:text-xl laptop:leading-8"
                )}
              >
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0">
                      <FaCheck size={16} className="fill-vivid-03" />
                    </div>
                    <HighlightedText
                      text={item.text}
                      highlight={item.highlight}
                      className="text-neutral-03 text-base laptop:text-lg leading-[26px]"
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p
          className={cn(
            "text-base laptop:text-lg leading-[26px] font-medium pt-4 border-t border-neutral-05 mt-auto",
            isPrimary ? "text-vivid-03" : "text-white"
          )}
        >
          {result}
        </p>
      </div>
    </ModelWrapper>
  );
}

export default function OfferModelsSection() {
  const { t } = useTranslation();

  const modelA = {
    title: t("products.squad-ia.offerModels.modelA.title"),
    titleHighlight: t("products.squad-ia.offerModels.modelA.titleHighlight", { defaultValue: "" }) || null,
    subtitle: t("products.squad-ia.offerModels.modelA.subtitle"),
    sections: [
      {
        title: t("products.squad-ia.offerModels.modelA.howItWorks.title"),
        items: [
          {
            text: t("products.squad-ia.offerModels.modelA.howItWorks.items.item1"),
            highlight: null,
          },
          {
            text: t("products.squad-ia.offerModels.modelA.howItWorks.items.item2"),
            highlight: t("products.squad-ia.offerModels.modelA.howItWorks.items.item2Highlight", { defaultValue: "" }) || null,
          },
          {
            text: t("products.squad-ia.offerModels.modelA.howItWorks.items.item3"),
            highlight: null,
          },
          {
            text: t("products.squad-ia.offerModels.modelA.howItWorks.items.item4"),
            highlight: t("products.squad-ia.offerModels.modelA.howItWorks.items.item4Highlight", { defaultValue: "" }) || null,
          },
        ],
      },
      {
        title: t("products.squad-ia.offerModels.modelA.journey.title"),
        items: [
          { text: t("products.squad-ia.offerModels.modelA.journey.items.item1"), highlight: null },
          { text: t("products.squad-ia.offerModels.modelA.journey.items.item2"), highlight: null },
          { text: t("products.squad-ia.offerModels.modelA.journey.items.item3"), highlight: null },
          { text: t("products.squad-ia.offerModels.modelA.journey.items.item4"), highlight: null },
        ],
      },
    ],
    result: t("products.squad-ia.offerModels.modelA.result"),
  };

  const modelB = {
    title: t("products.squad-ia.offerModels.modelB.title"),
    titleHighlight: null,
    subtitle: t("products.squad-ia.offerModels.modelB.subtitle"),
    sections: [
      {
        title: t("products.squad-ia.offerModels.modelB.included.title"),
        items: [
          { text: t("products.squad-ia.offerModels.modelB.included.items.item1"), highlight: null },
          { text: t("products.squad-ia.offerModels.modelB.included.items.item2"), highlight: null },
          { text: t("products.squad-ia.offerModels.modelB.included.items.item3"), highlight: null },
          { text: t("products.squad-ia.offerModels.modelB.included.items.item4"), highlight: null },
        ],
      },
    ],
    result: t("products.squad-ia.offerModels.modelB.result"),
  };

  return (
    <section className="text-white bg-background-02">
      <div className="container-blog">
        <div className="flex flex-col items-center justify-center">
          <Line className="border-vivid-03" />

          <Subtitle className="mt-4 mb-0 text-center text-white laptop:mt-6 laptop:mb-0">
            <h2>{t("products.squad-ia.offerModels.title")}</h2>
          </Subtitle>

          <Text className="mx-auto text-center max-w-[600px] mt-4">
            {t("products.squad-ia.offerModels.description")}
          </Text>
        </div>

        <div
          className={cn(
            "flex flex-col items-stretch justify-center gap-16 mt-[64px]",
            "laptop:flex-row laptop:gap-10 laptopL:mt-[88px]"
          )}
        >
          <ModelCard {...modelA} variant="primary" />
          <ModelCard {...modelB} variant="secondary" />
        </div>
      </div>
    </section>
  );
}
